import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { PieChart as PieIcon } from 'lucide-react';

/**
 * Lightweight SVG donut chart that visualizes the breakdown of users by
 * `auth_provider` (email / google / facebook / apple / unknown).
 *
 * No chart library — pure SVG so we don't ship a heavy dep just for one chart.
 */
const PROVIDER_META = {
  email:    { label: 'Email',    color: '#00E5CC' },
  google:   { label: 'Google',   color: '#EA4335' },
  facebook: { label: 'Facebook', color: '#1877F2' },
  apple:    { label: 'Apple',    color: '#111111' },
  unknown:  { label: 'Other',    color: '#9CA3AF' },
};

function describeArc(cx, cy, r, startAngle, endAngle) {
  const polar = (angle) => {
    const a = (angle - 90) * (Math.PI / 180);
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  };
  const start = polar(endAngle);
  const end = polar(startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return [
    'M', start.x, start.y,
    'A', r, r, 0, largeArc, 0, end.x, end.y,
  ].join(' ');
}

export const SignInProviderPie = ({ usersByProvider = {} }) => {
  // Normalize: collapse anything not in our known list into "unknown"
  const counts = { email: 0, google: 0, facebook: 0, apple: 0, unknown: 0 };
  Object.entries(usersByProvider || {}).forEach(([k, v]) => {
    const key = (k in counts) ? k : 'unknown';
    counts[key] += Number(v) || 0;
  });

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const segments = Object.entries(counts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ key: k, count: v, pct: total ? (v / total) * 100 : 0 }));

  // Build arc segments
  const cx = 80, cy = 80, r = 64, strokeWidth = 22;
  let cursor = 0;
  const arcs = segments.map((seg) => {
    const start = (cursor / 100) * 360;
    cursor += seg.pct;
    const end = (cursor / 100) * 360;
    // Avoid full-circle SVG arc bug (360deg = identical points → no arc)
    const safeEnd = end - start >= 359.999 ? end - 0.01 : end;
    return {
      ...seg,
      d: describeArc(cx, cy, r, start, safeEnd),
      color: PROVIDER_META[seg.key].color,
      label: PROVIDER_META[seg.key].label,
    };
  });

  return (
    <Card className="mb-8" data-testid="signin-method-pie-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <PieIcon className="h-5 w-5 text-[#00E5CC]" />
          Sign-in Methods
        </CardTitle>
        <CardDescription>How registered users authenticate</CardDescription>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="signin-pie-empty">
            No user data yet.
          </p>
        ) : (
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <svg
              width="160"
              height="160"
              viewBox="0 0 160 160"
              role="img"
              aria-label="Sign-in method distribution"
              data-testid="signin-pie-svg"
            >
              {/* Background ring */}
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#E5E7EB" strokeWidth={strokeWidth} />
              {arcs.map((a) => (
                <path
                  key={a.key}
                  d={a.d}
                  fill="none"
                  stroke={a.color}
                  strokeWidth={strokeWidth}
                  strokeLinecap="butt"
                />
              ))}
              <text
                x={cx}
                y={cy - 4}
                textAnchor="middle"
                className="fill-foreground"
                style={{ fontSize: 22, fontWeight: 700 }}
              >
                {total}
              </text>
              <text
                x={cx}
                y={cy + 16}
                textAnchor="middle"
                className="fill-muted-foreground"
                style={{ fontSize: 11 }}
              >
                users
              </text>
            </svg>

            <ul className="flex-1 w-full space-y-2" data-testid="signin-pie-legend">
              {Object.keys(PROVIDER_META).map((k) => {
                const cnt = counts[k];
                const pct = total ? (cnt / total) * 100 : 0;
                return (
                  <li
                    key={k}
                    className="flex items-center justify-between text-sm"
                    data-testid={`signin-pie-row-${k}`}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-sm"
                        style={{ background: PROVIDER_META[k].color }}
                      />
                      <span className="font-medium">{PROVIDER_META[k].label}</span>
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {cnt} <span className="opacity-60">({pct.toFixed(1)}%)</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SignInProviderPie;
